import ButtonBase from 'components/partials/button'
import InputBase from 'components/partials/inputBase'
import { IAgencyGroup, IAgencyGroupJobConfig } from 'pages/agency-group-management/interface'
import React, { useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import IconTrash from 'assets/images/icon-trash-active.svg'
import {
  ADD_OR_EDIT_AGENCY_GROUP_JOB_CONFIG,
  AgencyGroupJobTypeEnum,
  DELETE_CONFIG_AGENCY_GROUP_BY_JOB_TYPE,
  IAgencyGroupJobConfigInput,
} from 'queries/admin/agencyGroup'
import { useMutation } from '@apollo/client'
import { notify } from 'components/toastify'
import { STATUS } from 'constants/messages'
import { WebhookFormValues } from '../interface'
import axiosClient from 'network/axiosClient'
import { isArray, isEqual } from 'lodash'
import ConfirmDeleteDialog from 'pages/agency-group-management/dialogs/confirmDeleteDialog'
import { useSession } from 'helpers/checkSession'

interface Props {
  currentAgencyGroup: IAgencyGroup
  agencyGroupJobConfigs: IAgencyGroupJobConfig
  webhookForwardData: Record<string, any>
  refetchData: () => void
}

const CUSTOM_JOB_TYPE = AgencyGroupJobTypeEnum.FORWARD_WEBHOOK

const EditForwardWebhookConfig: React.FC<Props> = ({
  currentAgencyGroup,
  agencyGroupJobConfigs,
  webhookForwardData,
  refetchData,
}) => {
  const { handleErrorSession } = useSession()
  const [isEditMode, setIsEditMode] = useState(false)
  const [isOpenDialogConfirmDelete, setIsOpenDialogConfirmDelete] = useState(false)
  const [originalData, setOriginalData] = useState(webhookForwardData)

  const [addOrEditAgencyGroupJobConfig] = useMutation(ADD_OR_EDIT_AGENCY_GROUP_JOB_CONFIG)
  const [deleteConfigAgencyGroupByJobType] = useMutation(DELETE_CONFIG_AGENCY_GROUP_BY_JOB_TYPE)

  const { control, getValues, setValue, reset, setError, clearErrors } = useForm<WebhookFormValues>(
    {
      defaultValues: {
        url: originalData.url ?? '',
        headers: Array.isArray(originalData.headers) ? originalData.headers : [],
      },
    },
  )

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'headers',
  })

  const onClickEditBtn = () => {
    setIsEditMode(true)
  }

  const onClickRemove = () => {
    setIsOpenDialogConfirmDelete(true)
  }

  const onClickTest = () => {
    const { headers, url } = originalData

    let headerRequest = headers
    if (isArray(headers)) {
      headerRequest = headers.reduce(
        (acc: Record<string, string>, { key, value }: { key: string; value: string }) => {
          acc[key] = value
          return acc
        },
        {},
      )
    }

    axiosClient
      .post('/agencyGroup/proxy/test', {
        url,
        headers: headerRequest,
        body: 'this is a test',
      })
      .then(() => {
        notify('Send test successfully!', STATUS.SUCCESS)
      })
      .catch(() => {
        notify('Something wrong', STATUS.ERROR)
      })
  }

  const handleChangeEditMode = (isEdit: boolean) => {
    setIsEditMode(isEdit)
  }

  const handleSave = () => {
    const data = getValues()
    const keys = []
    let hasEmptyHeader = false
    let hasEmptyUrl = false
    let emptyIndex = 0

    for (let i = 0; i < data.headers.length; i++) {
      if (data.headers[i].key) {
        keys.push(data.headers[i].key.trim())
      } else {
        hasEmptyHeader = true
        emptyIndex = i
        break
      }
    }

    if (hasEmptyHeader) {
      setError(`headers.${emptyIndex}.key`, {
        type: 'manual',
        message: 'Header key cannot be empty',
      })
    } else {
      clearErrors('headers')
    }

    if (data.url.length === 0) {
      hasEmptyUrl = true
      setError(`url`, {
        type: 'manual',
        message: 'URL is required',
      })
    } else {
      clearErrors('url')
    }

    if (hasEmptyUrl || hasEmptyHeader) return

    // const keys = data.headers.map((item) => item.key.trim()).filter(Boolean)

    //  Get dup keys
    const keyCount: Record<string, number[]> = {}
    keys.forEach((key, index) => {
      if (!keyCount[key]) keyCount[key] = []
      keyCount[key].push(index)
    })

    const duplicatedKeys = Object.entries(keyCount).filter(([, indexes]) => indexes.length > 1)

    if (duplicatedKeys.length > 0) {
      // notify(
      //   `Duplicate header key(s): ${duplicatedKeys.map(([key]) => key).join(', ')}`,
      //   STATUS.ERROR,
      // )

      // Set error to the duplicate expect the first one
      duplicatedKeys.forEach(([, indexes]) => {
        indexes.slice(1).forEach((dupIndex) => {
          setError(`headers.${dupIndex}.key`, {
            type: 'manual',
            message: 'Duplicate key',
          })
        })
      })

      return
    }

    // Convert headers array to object
    const headersObject = data.headers.reduce((acc, item) => {
      if (item.key) acc[item.key] = item.value
      return acc
    }, {} as Record<string, string>)

    const finalData = {
      ...data,
      headers: headersObject,
    }

    const customConfig: IAgencyGroupJobConfigInput = {
      emailTemplateURL: null,
      htmlTemplateString: null,
      subEmailManagementConfigId: null,
      customEmailType: null,
      customJobType: CUSTOM_JOB_TYPE,
      forwardWebhook: finalData,
      forwardEmail: null,
      forwardFormatTemplate: null,
    }

    addOrEditAgencyGroupJobConfig({
      variables: {
        data: {
          agencyGroupId: currentAgencyGroup.agencyGroupId,
          customJobType: CUSTOM_JOB_TYPE,
          customConfigs: [customConfig],
        },
      },
      onCompleted: () => {
        handleChangeEditMode(false)
        notify('Email configuration updated successfully!', STATUS.SUCCESS)
      },
      onError: (error) => {
        if (error.message) {
          handleErrorSession(error, error.message)
        } else {
          notify('Something wrong', STATUS.ERROR)
        }
      },
    })

    setOriginalData(finalData)
    setIsEditMode(false)
    clearErrors()
  }

  const handleCancel = () => {
    if (originalData) {
      reset(originalData)
    }
    setIsEditMode(false)
  }

  const handleAddMore = () => {
    const data = getValues()
    const hasEmptyValue = data.headers.find((item) => item.key === '')
    if (hasEmptyValue) {
      setError(`headers.${data.headers.length}.key`, {
        type: 'manual',
        message: 'Empty value',
      })
    } else {
      clearErrors()
      append({ key: '', value: '' })
    }
  }

  const handleDeleteForwardWebhookConfig = () => {
    deleteConfigAgencyGroupByJobType({
      variables: {
        agencyGroupId: currentAgencyGroup.agencyGroupId,
        customJobType: CUSTOM_JOB_TYPE,
      },
      onCompleted: (data) => {
        if (data.deleteConfigAgencyGroupByJobType) {
          notify('Remove successfully', STATUS.SUCCESS)
        } else {
          notify('Something wrong, please try again', STATUS.ERROR)
        }
        setIsOpenDialogConfirmDelete(false)
        refetchData()
      },

      onError: (error) => {
        if (error.message) {
          handleErrorSession(error, error.message)
        } else {
          notify('Something wrong', STATUS.ERROR)
        }
      },
    })
  }

  const handleCloseConfirmDialog = () => {
    setIsOpenDialogConfirmDelete(false)
  }

  useEffect(() => {
    if (!isEqual(webhookForwardData, originalData)) {
      setOriginalData(webhookForwardData)
      setValue('url', webhookForwardData?.url)
      setValue('headers', webhookForwardData?.headers)
    }
  }, [webhookForwardData])

  return (
    <div>
      <div className="flex justify-end">
        {isEditMode ? (
          <div className="space-x-1">
            <ButtonBase onClick={handleSave}>Save</ButtonBase>
            <ButtonBase onClick={handleCancel}>Cancel</ButtonBase>
          </div>
        ) : (
          <div className="space-x-1">
            <ButtonBase onClick={onClickTest}>Test</ButtonBase>
            <ButtonBase
              className="bg-secondary-2 hover:bg-secondary-bold2"
              bgHover="Remove all config"
              onClick={onClickRemove}
            >
              Remove
            </ButtonBase>
            <ButtonBase onClick={onClickEditBtn}>Edit</ButtonBase>
          </div>
        )}
      </div>

      {agencyGroupJobConfigs && webhookForwardData && (
        <form>
          <div className="flex flex-col">
            <span>
              <strong>Webhook URL</strong>
            </span>
            <Controller
              name="url"
              control={control}
              render={({ field, fieldState }) => (
                <div className="w-full">
                  <InputBase className="w-full" {...field} disable={!isEditMode} />
                  {fieldState.error && (
                    <span className="text-red-500 text-sm">{fieldState.error.message}</span>
                  )}
                </div>
              )}
            />
          </div>

          <div className="flex flex-col pt-5">
            <span>
              <strong>Header</strong>
            </span>
            {fields?.length > 0 ? (
              <div className="space-y-2">
                {fields?.map((field, index) => (
                  <div key={field.id} className="flex w-full space-x-2">
                    <Controller
                      name={`headers.${index}.key`}
                      control={control}
                      defaultValue={field.key}
                      render={({ field, fieldState }) => (
                        <div className="w-[20%]">
                          <InputBase className="w-full" {...field} disable={!isEditMode} />
                          {fieldState.error && (
                            <span className="text-red-500 text-sm">{fieldState.error.message}</span>
                          )}
                        </div>
                      )}
                    />

                    <Controller
                      name={`headers.${index}.value`}
                      control={control}
                      defaultValue={field.value}
                      render={({ field }) => (
                        <InputBase className="w-[85%]" {...field} disable={!isEditMode} />
                      )}
                    />
                    {isEditMode && (
                      <img
                        src={IconTrash}
                        alt="iconTrash"
                        className="cursor-pointer"
                        onClick={() => remove(index)}
                      />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="pt-5 pb-3 text-center text-body1 text-neutral-4">No header data.</div>
            )}
          </div>
          {isEditMode && (
            <div className="pt-5">
              <ButtonBase onClick={handleAddMore}>Add More Header</ButtonBase>
            </div>
          )}
        </form>
      )}

      {/**Delete dialog */}
      <ConfirmDeleteDialog
        title="Confirm to delete webhook forward config?"
        handleCloseBtn={handleCloseConfirmDialog}
        handleConfirmBtn={handleDeleteForwardWebhookConfig}
        isOpenDialog={isOpenDialogConfirmDelete}
        dialogStyle={{ width: '470px', maxHeight: '200px' }}
      />
    </div>
  )
}

export default EditForwardWebhookConfig
